const DAY = new Intl.DateTimeFormat('pl-PL', { timeZone: 'Europe/Warsaw', day: 'numeric', month: 'long', year: 'numeric' });

/** `block`: an equipment block (owner 2026-10-24 15:29) — the gear is out of the warehouse. */
export type Occupancy = { groupId: string | null; groupLabel: string; kind: 'soft' | 'hard'; quantity: number; start: string; end: string; block?: true };

const WARSAW_DAY = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Warsaw' });
/** The Warsaw day ('YYYY-MM-DD') of an instant, or null for an empty / invalid one. */
const warsawDayOf = (ms: number): string | null => (Number.isFinite(ms) ? WARSAW_DAY.format(new Date(ms)) : null);

/**
 * Owner 2026-10-24 15:35 („Info o zajętości dłuższej na czerwono wyraźnie”): a block, or an occupation running beyond the
 * asking days (`firstDay`–`lastDay`), is shown in clear red.
 */
export function isLongBusy(o: Pick<Occupancy, 'start' | 'end' | 'block'>, firstDay?: string, lastDay?: string): boolean {
  if (o.block) return true;
  if (!firstDay || !lastDay || !o.start || !o.end) return false;
  const first = warsawDayOf(new Date(o.start).getTime());
  const last = warsawDayOf(new Date(o.end).getTime() - 1);
  return first !== null && last !== null && (first < firstDay || last > lastDay);
}

/** „wstępna” / „pewna”, or for a block „nie ma w magazynie”. */
export const busyKind = (o: Pick<Occupancy, 'kind' | 'block'>) => (o.block ? 'nie ma w magazynie' : kindLabel(o.kind));

export const kindLabel = (kind: 'soft' | 'hard') => (kind === 'hard' ? 'pewna' : 'wstępna');

/** Days of a reservation as shown to people: the end is exclusive, so the last day is the one before it. */
export function formatDays(start: string, end: string): string {
  const first = DAY.format(new Date(start));
  const last = DAY.format(new Date(new Date(end).getTime() - 1));
  return first === last ? first : `${first} – ${last}`;
}

/** "Zajęte w": the reservations overlapping the checked days (`from`–`to`). */
export function BusyList({ busyIn, from, to }: { busyIn: Occupancy[]; from?: string; to?: string }) {
  return (
    <section className="panel" aria-label="Zajęte w">
      <h3>Zajęte w</h3>
      {busyIn.length === 0 ? (
        <p className="muted">Nic nie zajmuje tej pozycji w wybranych dniach.</p>
      ) : (
        <ul className="list">
          {busyIn.map((o, i) => (
            <li key={`${o.groupId}-${i}`} className={isLongBusy(o, from, to) ? 'row busy-long' : 'row'}>
              <div className="row-main">
                <strong>{o.groupLabel}</strong> <span className="chip">{busyKind(o)}</span>
                <div className="muted">
                  Ilość: {o.quantity} · {formatDays(o.start, o.end)}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Days given as 'YYYY-MM-DD' (as the manual reservation list sends them); a role that may not see them gets nothing. */
export function formatDayRange(firstDay?: string, lastDay?: string): string {
  if (!firstDay || !lastDay) return '';
  const fmt = (d: string) => DAY.format(new Date(`${d}T12:00:00Z`));
  return firstDay === lastDay ? fmt(firstDay) : `${fmt(firstDay)} – ${fmt(lastDay)}`;
}
